import { Component, ReactElement, Ref } from 'react'

export interface Validatable {
  readonly validity: ValidityState
  readonly validationMessage: string
  readonly setCustomValidity: (message: string) => void
  readonly checkValidity: () => boolean
}

export interface ValidationProps {
  /**
   * The validation states of all fields in the form. This is intended
   * for complex validation where the field's validity depends on the
   * validity of other fields
   */
  readonly validationStates: { readonly [key: string]: ValidityState }

  /**
   * The validation state for the field being validated
   */
  readonly validity: ValidityState
}

type Validator = (props: ValidationProps, ref: Validatable) => boolean

export interface FormFieldProps<R extends Validatable> {
  /**
   * The ref callback for the input
   */
  readonly inputRef: Ref<R>

  /**
   * Callback that should be invoked when a user leaves an input field
   */
  readonly onBlur: (e: React.FocusEvent<any>) => void

  /**
   * Callback that should be invoked when field is visited
   */
  readonly onFocus: () => void

  /**
   * The error message for the field. If not defined, the field is valid
   */
  readonly errorMessage?: string
}

interface ErrorMessageGetterProps {
  /**
   * The DOM validation state for the field. Since it depends on whether
   * a ref was registered for the field and whether or not the field
   * has been interacted with, custom error message getters need to handle
   * the case where this field is not present
   */
  readonly validity?: ValidityState

  /**
   * The validationMessage, if any from the DOM API
   */
  readonly validationMessage?: string
}

export type MessageGetter = (props: ErrorMessageGetterProps) => string

interface GetFormFieldProps {
  /**
   * An id that is unique for the input field being registered and stable across
   * renders
   */
  readonly id: string

  /**
   * A custom validator for this field. Validation is performed when a component
   * updates due to a change in the form's value.
   */
  readonly validator?: Validator

  /**
   * Used to specify a custom error message if the field is invalid
   */
  readonly getErrorMessage?: MessageGetter

  readonly onBlur?: (e: React.FocusEvent<any>) => void
}

interface FormManagerChildProps {
  /**
   * True when all fields of the form are valid
   */
  readonly formIsValid: boolean
  readonly setCustomValidity?: (id: string, validity: boolean) => void

  /**
   * Resets the form validation state
   */
  readonly resetValidationState: () => void

  /**
   * Gets the props for a field
   */
  readonly getFormFieldProps: GetFormFieldPropsFn
}

export type GetFormFieldPropsFn = <R extends Validatable>(
  props: GetFormFieldProps
) => FormFieldProps<R>

interface State {
  readonly blurredStates: { readonly [key: string]: number }
  readonly customValidity: { readonly [key: string]: boolean }
  readonly focusedStates: { readonly [key: string]: number }
  readonly validationStates: { readonly [key: string]: ValidityState }
  readonly isValid: { readonly [key: string]: boolean }
  readonly errorMessages: { readonly [key: string]: string }
  readonly formIsValid: boolean
}

interface Props {
  readonly children: (props: FormManagerChildProps) => ReactElement<any>
  readonly value: unknown
}

/**
 * FormValidator is used to get the validation states from input elements
 */
export class FormValidator extends Component<Props, State> {
  public readonly state: State = this.getInitialState()

  private inputRefs: {
    [key: string]: Validatable | undefined | null
  } = {}
  private inputValidators: {
    [key: string]: Validator | undefined
  } = {}
  private errorMessageGetters: {
    [key: string]: MessageGetter | undefined
  } = {}

  public render() {
    return this.props.children({
      formIsValid: this.state.formIsValid,
      setCustomValidity: this.setCustomValidity,
      resetValidationState: () => this.setState(this.getInitialState()),
      getFormFieldProps: ({ id, validator, getErrorMessage, onBlur }) => {
        const { errorMessages } = this.state

        this.inputValidators[id] = validator
        this.errorMessageGetters[id] = getErrorMessage

        const errorMessage = errorMessages[id]

        return {
          errorMessage,
          inputRef: (ref) => this.registerInputRef(id, ref),
          onBlur: (e) => {
            this.onBlur(id)
            if (onBlur) {
              onBlur(e)
            }
          },
          onFocus: () => this.onFocus(id)
        }
      }
    })
  }

  public componentDidMount() {
    this.validate()
  }

  public componentDidUpdate(prevProps, prevState: State) {
    const { blurredStates, focusedStates } = this.state
    const { value } = this.props
    if (
      prevProps.value !== value ||
      prevState.blurredStates !== blurredStates ||
      prevState.focusedStates !== focusedStates
    ) {
      this.validate()
    }
  }

  private validate = () => {
    const validationStates: { [id: string]: ValidityState } = {}
    const isValid: { [id: string]: boolean } = {}
    const errorMessages: { [id: string]: string } = {}
    let formIsValid = true

    Object.keys(this.inputRefs).forEach((id) => {
      const ref = this.inputRefs[id]

      // ref will be null if the associated element/component is no longer
      // rendered
      if (!ref) {
        return
      }

      // reset custom validity before each check
      ref.setCustomValidity('')

      const validity = ref.validity
      validationStates[id] = validity

      const { focusedStates, blurredStates } = this.state

      const validator: Validator | undefined = this.inputValidators[id]
      const fieldIsValid = validator
        ? validator(
            {
              validity,
              validationStates
            },
            ref
          )
        : ref.checkValidity()

      if (!fieldIsValid) {
        formIsValid = false
      }

      isValid[id] = fieldIsValid

      const wasVisited = focusedStates[id] > 0
      const wasLeft = blurredStates[id] > 0

      let validationMessage
      if (!fieldIsValid && wasVisited && wasLeft) {
        validationMessage = ref.validationMessage

        const errorMessageGetter: MessageGetter | undefined =
          this.errorMessageGetters[id]
        if (errorMessageGetter) {
          const customErrorMessage = errorMessageGetter({
            validity,
            validationMessage
          })
          if (customErrorMessage) {
            validationMessage = customErrorMessage
            ref.setCustomValidity(validationMessage)
          }
        }
      }

      errorMessages[id] = validationMessage
    })

    if (formIsValid) {
      const { customValidity } = this.state

      Object.keys(customValidity).forEach((key) => {
        if (!!!customValidity[key]) {
          formIsValid = false
        }
      })
    }

    this.setState({ isValid, validationStates, errorMessages, formIsValid })
  }

  private setCustomValidity = (id: string, validity: boolean) => {
    this.setState((prevState) => {
      return {
        customValidity: Object.assign({}, prevState.customValidity, {
          [id]: validity
        })
      }
    })
  }

  private onBlur(id: string) {
    this.setState((prevState) => {
      return {
        blurredStates: Object.assign({}, prevState.blurredStates, {
          [id]: true
        })
      }
    })
  }

  private onFocus(id) {
    this.setState((prevState) => {
      return {
        focusedStates: Object.assign({}, prevState.focusedStates, {
          [id]: true
        })
      }
    })
  }

  private registerInputRef(id: string, ref: Validatable | null) {
    this.inputRefs[id] = ref
  }

  private getInitialState(): State {
    return {
      validationStates: {},
      focusedStates: {},
      customValidity: {},
      blurredStates: {},
      isValid: {},
      errorMessages: {},
      formIsValid: false
    }
  }
}
